import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import ChequeItem from './Components/ChequeItem';
import Bill from './Bill';
import Logs from './Logs';
import BookingDetails from './Components/Bookings/BookingDetails';
import NewCheque from './Components/NewCheque';
import { useButton, useI18n, useStaffCan } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { DateTime } from 'luxon';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import ArrowIcon from '~/Assets/icons/arrow_forward.svg';
import { Box, TransparentButton } from '~Styles';
import { useBookings } from '~Providers/BookingsProvider';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';

const Wrapper = styled.div<{
  fullHeight: boolean;
}>`
  display: flex;
  flex-flow: row nowrap;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  box-shadow: -24px 0px 64px 0px rgba(0, 0, 0, 0.32);
`;

const SideBar = styled.div`
  padding: 16px;
  background-color: var(--colorBlueGray900);
  width: 136px;
  box-sizing: border-box;
  height: 100%;
  overflow: auto;
  overflow-x: hidden;
`;

const Button = styled(TransparentButton)<{
  active: boolean;
}>`
  border-radius: 0;
  background-color: ${(props) =>
    props.active ? 'var(--colorBlueGray1300)' : 'var(--colorBlueGray1200)'};
  color: ${(props) => (props.active ? 'var(--colorBlueGray100)' : 'var(--colorBlueGray400)')};
  transform: none;
  text-transform: none;
`;

const Booking = styled.div<{ selected: boolean }>`
  margin: 16px 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 4px;
  width: 88px;
  border-radius: 4px;
  height: 128px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
  border: 1px solid;
  border-color: ${({ selected }) => (selected ? 'var(--colorUIYellow)' : 'transparent')};
  box-shadow: ${({ selected }) => (selected ? '0px 0px 16px 0px rgba(0, 0, 0, 0.32)' : 'none')};
  box-sizing: border-box;
  position: relative;

  &:after {
    content: '';
    height: 24px;
    width: 24px;
    mask-image: url(${ArrowIcon});
    mask-position: center;
    mask-size: contain;
    mask-repeat: no-repeat;
    background-color: var(--colorBlueGray200);
    position: absolute;
    top: 52px;
    right: -28px;
    opacity: ${(props) => (props.selected ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
  }
`;

interface Props {
  table: {
    id: string;
    name: string;
  } | null;
  allCheques: any;
  onSelectCheque: (id: string | null) => void;
  selectedCheque: string | null;
  isBongVisible?: boolean;
  homelessItems?: boolean;
  showBong: () => void;
  showLogin: () => void;
  onDelete?: () => void;
}

const Cheques: React.FunctionComponent<Props> = ({
  selectedCheque,
  isBongVisible,
  table,
  allCheques,
  onSelectCheque,
  homelessItems,
  showBong,
  showLogin,
  onDelete,
}) => {
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const [showBill, setShowBill] = useState(true);
  const [
    selectedBooking,
    setSelectedBooking,
  ] = useState<GetBookings_operativeViewer_bookingsForVenueAndDate | null>(null);
  const tableCheques = allCheques.filter((item: any) => item.tableId === table?.id);
  const [showNewCheckAnimation, setShowNewCheckAnimation] = useState(false);
  const staffCan = useStaffCan();
  const staffCanMutate = table && staffCan.mutateTable(table?.id);
  const bookings = useBookings();

  useEffect(() => {
    if (!tableCheques.some((tableCheque: any) => tableCheque.id === selectedCheque)) {
      onSelectCheque(tableCheques[0]?.id || null);
    }
    setShowNewCheckAnimation(false);
    setSelectedBooking(null);
  }, [table]);

  // Deselect booking when a cheque is selected
  useEffect(() => {
    if (selectedCheque) {
      setSelectedBooking(null);
    }
  }, [selectedCheque]);

  const enableShowCheckAnimation = () => {
    setShowNewCheckAnimation(true);
  };

  const acceptedBookings = bookings.today.filter(
    (booking) => booking.status === 'ACCEPTED' && booking.tableIds.includes(table?.id || ''),
  );

  // Sort by date and startTime string
  const sortedBookings = [...acceptedBookings]?.sort(
    (a: any, b: any) =>
      DateTime.fromISO(a.date)
        .set({
          hour: parseInt(a.startTime.split(':')[0]),
          minute: parseInt(a.startTime.split(':')[1]),
        })
        .toMillis() -
      DateTime.fromISO(b.date)
        .set({
          hour: parseInt(b.startTime.split(':')[0]),
          minute: parseInt(b.startTime.split(':')[1]),
        })
        .toMillis(),
  );

  const billButton = useButton({
    onClick: () => setShowBill(true),
  });

  const logsButton = useButton({
    onClick: () => setShowBill(false),
  });

  return (
    <Wrapper fullHeight={Boolean(table?.id)}>
      {!homelessItems && (
        <SideBar>
          <Heading level={4} color="var(--colorBlueGray100)">
            {i18n('TM.Cheques')}
          </Heading>
          {tableCheques.map((item: any, index: number) => {
            return (
              <ChequeItem
                key={item.id}
                chequeId={item.id}
                table={table}
                ownerId={item.owner}
                chequeName={item.name ? item.name : '#' + item.id.substring(0, 4)}
                selected={item.id === selectedCheque}
                setSelectedCheque={onSelectCheque}
                showNewCheckAnimation={index === tableCheques.length - 1 && showNewCheckAnimation}
                showLogin={showLogin}
              />
            );
          })}
          {table && staffCanMutate && (
            <NewCheque
              tableID={table.id}
              selectCheque={onSelectCheque}
              enableShowCheckAnimation={enableShowCheckAnimation}
              nrOfCheques={tableCheques.length}
              tableCheques={tableCheques}
              showBong={showBong}
              showLogin={showLogin}
              disableCoverage={storage.hideCoverageModal}
            />
          )}

          {acceptedBookings.length > 0 && (
            <>
              <Heading level={4} color="var(--colorBlueGray100)">
                {i18n('Bookings')}
              </Heading>
              {sortedBookings.map((booking) => (
                <Booking
                  key={booking.id}
                  selected={selectedBooking?.id === booking.id}
                  onTouchStart={() => {
                    setSelectedBooking(booking);
                    setShowBill(true);
                    onSelectCheque(null); // Deselect any selected cheque
                  }}>
                  <Heading level={3} color="var(--colorWhite)">
                    {booking.startTime}
                  </Heading>
                  <Paragraph level={3} color="var(--colorBlueGray200)">
                    {booking.guestName}
                  </Paragraph>
                </Booking>
              ))}
            </>
          )}
        </SideBar>
      )}
      <Content>
        {selectedBooking ? (
          <BookingDetails
            booking={selectedBooking}
            tableId={table?.id || ''}
            onChequeCreated={(chequeId) => {
              onSelectCheque(chequeId);
              setSelectedBooking(null);
              enableShowCheckAnimation();
            }}
          />
        ) : (
          <>
            <Box display="flex">
              <Button
                active={showBill}
                {...billButton}
                display="flex"
                alignItems="center"
                justifyContent="center"
                size="large"
                fluid>
                {i18n('TM.Bill')}
              </Button>
              <Button
                active={!showBill}
                {...logsButton}
                display="flex"
                alignItems="center"
                justifyContent="center"
                size="large"
                fluid>
                {i18n('TM.Logs')}
              </Button>
            </Box>

            {table && showBill ? (
              <Bill
                setSelectedCheque={onSelectCheque}
                selectedCheque={selectedCheque}
                homelessItems={homelessItems}
                tableName={table.name}
                tableId={table.id}
                showLogin={showLogin}
                onDelete={onDelete}
                isBongVisible={isBongVisible}
              />
            ) : (
              <Logs selectedCheque={selectedCheque} table={table} />
            )}
          </>
        )}
      </Content>
    </Wrapper>
  );
};

export default React.memo(Cheques);
